import { useEffect, useRef, useState } from 'react'
import type { ChecklistReasonInput, Message, MessageOption } from '../../api/types'
import { formatClock } from '../../lib/format'
import { ChecklistPage } from './ChecklistPage'
import { finishChecklist, setTick } from './messageActions'
import { endedCaption, questionStatus } from './questions'
import { useConversation } from './ConversationContext'
import { ReasonsDialog } from './ReasonsDialog'
import { useExpired } from './useExpired'

interface Props {
  /** A bot's checklist. */
  checklist: Message
  /** The id of the checklist's words in the bubble; the checklist's other ids start with it. */
  labelId: string
  /** Done was sent from this device (sent or still sending). */
  doneHere: boolean
  /** The conversation's bot has a webhook, so it hears ticks and Done. */
  botAccepts: boolean
  /** Who sent it, for "Closed by Rota". */
  botName?: string | null
}

/** The text field that has focus, if one has. */
function textField(el: Element | null): HTMLElement | null {
  return el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement ? el : null
}

function without(record: Record<string, boolean>, key: string): Record<string, boolean> {
  const next = { ...record }
  delete next[key]
  return next
}

/**
 * A bot's checklist (docs/superpowers/specs/2026-10-03-checklists-design.md): in the conversation a compact bubble with the progress
 * and one button, which opens the checklist's own page to tick items as they go, saved one by one. Done asks why for each item not
 * ticked. The ticks being saved live here, so closing the page loses none.
 */
export function MessageChecklist({ checklist, labelId, doneHere, botAccepts, botName = null }: Props) {
  const items = checklist.options ?? []
  // Taps being saved: what each item will be once saved. The row shows it at once; a failed save drops it.
  const [saving, setSaving] = useState<Record<string, boolean>>({})
  const [paging, setPaging] = useState(false)
  // The message box, when it had focus (its keyboard up) as the button was pressed: the page gives it back as it closes, so the
  // keyboard comes back. Taken as the finger lands, since on iOS the tap moves focus onto the message before the click.
  const typingIn = useRef<HTMLElement | null>(null)
  const [returnTo, setReturnTo] = useState<HTMLElement | null>(null)
  const [asking, setAsking] = useState(false)
  const expired = useExpired(checklist.optionsExpireAt ?? null)
  const saved = new Set(checklist.ticks ?? [])
  const finished = Boolean(checklist.answered) || doneHere
  const status = finished ? 'answered' : questionStatus({ chosen: null, closedAt: checklist.optionsClosedAt ?? null, expired, botAccepts })
  const ticked = (id: string) => (id in saving ? saving[id] : saved.has(id))
  const count = items.filter((item) => ticked(item.id)).length
  const unticked = items.filter((item) => !ticked(item.id))
  const busy = Object.keys(saving).length > 0
  const captionId = `${labelId}-caption`

  // A save is over once the checklist says so (its event has arrived).
  useEffect(() => {
    const now = new Set(checklist.ticks ?? [])
    setSaving((current) => {
      const left = Object.fromEntries(Object.entries(current).filter(([id, want]) => now.has(id) !== want))
      return Object.keys(left).length === Object.keys(current).length ? current : left
    })
  }, [checklist.ticks])

  const toggle = (item: MessageOption) => {
    if (status !== 'open' || item.id in saving) return
    const want = !ticked(item.id)
    setSaving((current) => ({ ...current, [item.id]: want }))
    void setTick(checklist, item.id, want).then((ok) => {
      if (!ok) setSaving((current) => without(current, item.id))
    })
  }

  const progress = `${count} of ${items.length}`
  const caption =
    status === 'answered'
      ? `Finished${checklist.answered ? ` ${formatClock(checklist.answered.answeredAt)}` : ''} · ${progress}`
      : status === 'closed'
        ? `${endedCaption({ closedAt: checklist.optionsClosedAt ?? null, expireAt: checklist.optionsExpireAt ?? null, expired, botName })} · ${progress}`
        : `${progress} ticked${checklist.optionsExpireAt ? ` · Finish by ${formatClock(checklist.optionsExpireAt)}` : ''}`

  // Once finished, why each item not ticked was not done: from the Done reply, when this device has it.
  const { messagesById } = useConversation()
  const doneReply = checklist.answered ? messagesById.get(checklist.answered.messageId) : undefined
  const reasons = new Map((doneReply?.checklist?.notDone ?? []).map((n) => [n.id, n.reason]))

  const finish = (reasons: ChecklistReasonInput[]) => {
    setAsking(false)
    setPaging(false)
    void finishChecklist(checklist, reasons)
  }

  return (
    <div className="message-checklist">
      <p id={captionId} className="checklist-caption">
        {caption}
      </p>
      {/* A hold or a swipe that starts on the button presses it, as on question answers: no menu, no swipe to reply. */}
      <button
        type="button"
        className={status === 'open' ? 'button primary checklist-open' : 'button checklist-open'}
        aria-describedby={captionId}
        data-no-gestures
        onPointerDown={() => {
          typingIn.current = textField(document.activeElement)
        }}
        onClick={() => {
          setReturnTo(typingIn.current)
          typingIn.current = null
          setPaging(true)
        }}
      >
        {status !== 'open' ? 'View' : count === 0 ? 'Start checklist' : 'Continue'}
      </button>
      {paging ? (
        <ChecklistPage
          body={checklist.body}
          items={items}
          idPrefix={`${labelId}-page`}
          ticked={ticked}
          reasons={reasons}
          caption={caption}
          open={status === 'open'}
          busy={busy}
          onToggle={toggle}
          onDone={() => (unticked.length === 0 ? finish([]) : setAsking(true))}
          onClose={() => setPaging(false)}
          focusOnClose={returnTo}
        />
      ) : null}
      {/* The reasons go with the chance to finish: a checklist closed meanwhile takes them away and stays open, locked. */}
      {asking && status === 'open' ? <ReasonsDialog items={unticked} onCancel={() => setAsking(false)} onSend={finish} /> : null}
    </div>
  )
}
